import React from 'react';
import { CellContext } from '@tanstack/react-table';
import { utcToZonedTime } from 'date-fns-tz';

import Actions from '@components/common/Actions';
import AdaptiveTableCell from '@components/common/AdaptiveTableCell/AdaptiveTableCell';

import { formatDate } from '@utils/date';
import { convertBytesToSize } from '@utils/fileSizeFormatter';

import { IDownloadHistoryItem } from '@interfaces/downloadHistory/downloadHistoryDTO';

import { EFileStatusTypes } from '@enums/downloadHistory';

import colors from '@constants/colors';
import { formatTemplateWithTime, timezoneUTC } from '@constants/common';
import { columnSizes } from '@constants/table';

import tableStyles from '@components/common/Table/Table.module.scss';

interface IColumns {
	openDeleteConfirm: (file: IDownloadHistoryItem | null) => void;
	handleExportFile: (file: IDownloadHistoryItem | null) => void;
}

export const getColumns = ({
	openDeleteConfirm,
	handleExportFile,
}: IColumns) => [
	{
		header: 'File name',
		accessorKey: 'fileName',
		cell: ({ row: { original } }: CellContext<IDownloadHistoryItem, any>) => {
			const fileName = original.name;

			return <AdaptiveTableCell text={fileName}/>;
		},
	},
	{
		header: 'Size',
		accessorKey: 'size',
		cell: ({ getValue }: CellContext<IDownloadHistoryItem, any>) => {
			const value = getValue();
			const size = convertBytesToSize(value);

			return <AdaptiveTableCell text={size}/>;
		},
	},
	{
		header: 'Source',
		accessorKey: 'source',
		cell: ({ getValue }: CellContext<IDownloadHistoryItem, any>) => {

			const source = getValue().title;
			return <AdaptiveTableCell text={source}/>;
		},
	},
	{
		header: 'Status',
		accessorKey: 'status',
		enableSorting: false,
		cell: ({ getValue }: CellContext<IDownloadHistoryItem, any>) => {
			const status = getValue();
			const statusColor = status?.name === EFileStatusTypes.completed
				? colors.green70
				: colors.red50;

			return (
				<AdaptiveTableCell
					styleInline={{ color: statusColor }}
					text={status.title}
				/>
			);
		},
	},
	{
		header: 'Date',
		accessorKey: 'createdAt',
		cell: ({ getValue }: CellContext<IDownloadHistoryItem, any>) => {
			const value = getValue();
			const createdAt = formatDate(utcToZonedTime(value, timezoneUTC), formatTemplateWithTime);

			return <AdaptiveTableCell text={createdAt}/>;
		},
	},
	{
		header: () => <div className={tableStyles.centerColumnHeader}>Actions</div>,
		accessorKey: 'actions',
		enableSorting: false,
		size: columnSizes.size85,
		cell: ({ row: { original } }: CellContext<IDownloadHistoryItem, any>) => {
			const isDownloadDisable = original.status.name !== EFileStatusTypes.completed;

			const handleDelete = () => openDeleteConfirm(original);
			const downloadHandler = () => !isDownloadDisable && handleExportFile(original);

			return (
				<div className={tableStyles.actionCell}>
					<Actions
						deleteTooltip='Delete file'
						downloadHandler={downloadHandler}
						downloadTooltip='Download file'
						handleDelete={handleDelete}
						isDelete
						isDownload
						isDownloadDisable={isDownloadDisable}
					/>
				</div>
			);
		},
	},
];
